import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, betreuerByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { AngebotPosition, defaultAngebotPosition, moveUpAngebotPosition } from 'api/angebotPosition';
import { EditIcon, DeleteIcon, UpIcon, RepeatAllIcon, RepeatOneIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { formStyles } from 'styles/formStyles';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { Angebot } from 'api/angebot';
import { Termin } from 'api/termin';
import { authSelector } from 'store/auth/selector';
import { TerminEdit, TerminEditData } from 'components/Termin/TerminEdit';
import { AngebotPositionEdit } from './AngebotPositionEdit';
import { AngebotPositionDelete } from './AngebotPositionDelete';
import { AngebotPositionenHeader } from './AngebotPositionenHeader';

interface AngebotPositionenProps {
    kunde: Kunde;
    angebot: Angebot;
    aNr: string;
    angebotPositionen: AngebotPosition[];
    wiedervorlagen: Termin[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch: () => void;
}

export const AngebotPositionen: React.FC<AngebotPositionenProps> = ({
    kunde,
    angebot,
    aNr,
    angebotPositionen,
    wiedervorlagen,
    viewMode,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const auth = useSelector(authSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const [angebotPositionEditData, setAngebotPositionEditData] = React.useState<AngebotPosition | null>(null);
    const [angebotPositionDeleteData, setAngebotPositionDeleteData] = React.useState<AngebotPosition | null>(null);
    const [terminEditData, setTerminEditData] = React.useState<TerminEditData | null>(null);

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <AngebotPositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setAngebotPositionEditData({
                                ...defaultAngebotPosition,
                                aNr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {/* hide Reclay lines in short view */}
                    {angebotPositionen
                        .filter((angebotPosition) => viewMode === 'long' || angebotPosition.artikelGruppeNr !== 97)
                        .map((angebotPosition) => {
                            const wiedervorlage = (wiedervorlagen || []).find(
                                (wv) =>
                                    wv.kNr === kunde.nr &&
                                    wv.artikelGruppeNr === angebotPosition.artikelGruppeNr &&
                                    wv.artikelNr === angebotPosition.artikelNr,
                            );

                            return (
                                <tr key={angebotPosition.pos} className={formStyles.positionenRow}>
                                    <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                        {angebotPosition.pos}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenArtikelGruppeCell,
                                        )}
                                    >
                                        {(artikelGruppenByNr[angebotPosition.artikelGruppeNr] &&
                                            artikelGruppenByNr[angebotPosition.artikelGruppeNr]?.text) ||
                                            angebotPosition.artikelGruppeNr}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenArtikelNrCell,
                                        )}
                                    >
                                        <span>
                                            {angebotPosition.artikelGruppeNr && angebotPosition.artikelNr ? (
                                                <OpenLink
                                                    link={createRecordUrl(
                                                        'lager/artikel',
                                                        undefined,
                                                        getArtikelDetailId(
                                                            angebotPosition.artikelGruppeNr,
                                                            angebotPosition.artikelNr,
                                                        ),
                                                        undefined,
                                                        false,
                                                    )}
                                                    label={angebotPosition.artikelNr}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}
                                    >
                                        {angebotPosition.posText}
                                        {angebotPosition.posBem ? (
                                            <i>
                                                <br />
                                                {angebotPosition.posBem}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}
                                    >
                                        {angebotPosition.posEinh
                                            ? einheitenByCode[angebotPosition.posEinh].text || angebotPosition.posEinh
                                            : '?'}
                                        {angebotPosition.posEinh2 ? (
                                            <i>
                                                <br />
                                                V1:{' '}
                                                {einheitenByCode[angebotPosition.posEinh2].text ||
                                                    angebotPosition.posEinh2}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                        {angebotPosition.posEinh3 ? (
                                            <i>
                                                <br />
                                                V2:{' '}
                                                {einheitenByCode[angebotPosition.posEinh3].text ||
                                                    angebotPosition.posEinh3}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                        {angebotPosition.posEinh4 ? (
                                            <i>
                                                <br />
                                                V3:{' '}
                                                {einheitenByCode[angebotPosition.posEinh4].text ||
                                                    angebotPosition.posEinh4}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMengeCell,
                                        )}
                                    >
                                        {formatDecimal(angebotPosition.posMenge, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                    >
                                        {formatMoney(angebotPosition.posEkPreis, 3)}
                                        {angebotPosition.posEkPreis2 ? (
                                            <i>
                                                <br />
                                                V1: {formatMoney(angebotPosition.posEkPreis2, 3)}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                        {angebotPosition.posEkPreis3 ? (
                                            <i>
                                                <br />
                                                V2: {formatMoney(angebotPosition.posEkPreis3, 3)}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                        {angebotPosition.posEkPreis4 ? (
                                            <i>
                                                <br />
                                                V3: {formatMoney(angebotPosition.posEkPreis4, 3)}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                    >
                                        {formatMoney(angebotPosition.posEPreis, 3)}
                                        {angebotPosition.posEPreis2 ? (
                                            <i>
                                                <br />
                                                V1: {formatMoney(angebotPosition.posEPreis2, 3)}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                        {angebotPosition.posEPreis3 ? (
                                            <i>
                                                <br />
                                                V2: {formatMoney(angebotPosition.posEPreis3, 3)}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                        {angebotPosition.posEPreis4 ? (
                                            <i>
                                                <br />
                                                V3: {formatMoney(angebotPosition.posEPreis4, 3)}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                        title={'VK-Brutto: ' + formatMoney(angebotPosition.posBrutto)}
                                    >
                                        {formatMoney(angebotPosition.posNetto)}
                                    </td>
                                    {/*
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatMoney(angebotPosition.posBrutto)}
                                </td>
                                */}
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                        title={'VK-Brutto: ' + formatMoney(angebotPosition.posBrutto)}
                                    >
                                        {formatPercent(angebotPosition.posMwst)}
                                    </td>
                                    {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            {angebotPosition.artikelGruppeNr && angebotPosition.artikelNr ? (
                                                wiedervorlage ? (
                                                    <RepeatOneIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={() => {
                                                            setTerminEditData({
                                                                kNr: wiedervorlage.kNr,
                                                                zfKaNr: wiedervorlage.zfKaNr || 0,
                                                            });
                                                        }}
                                                    />
                                                ) : (
                                                    <RepeatAllIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={() => {
                                                            setTerminEditData({
                                                                kNr: kunde.nr,
                                                                zfKaNr: -1,
                                                                initUserKz: angebot.telBetreuer
                                                                    ? betreuerByNr[angebot.telBetreuer]?.kz || auth.kz
                                                                    : auth.kz,
                                                                initKaNr: angebot.kaNr,
                                                                initKaName: angebot.kaName,
                                                                initTerminArt: 'W',
                                                                initTerminBetreff:
                                                                    (artikelGruppenByNr[angebotPosition.artikelGruppeNr]
                                                                        ?.text || '?') +
                                                                    ' | ' +
                                                                    angebotPosition.artikelNr,
                                                                initArtikelGruppeNr: angebotPosition.artikelGruppeNr,
                                                                initArtikelNr: angebotPosition.artikelNr,
                                                                initPosText: angebotPosition.posText,
                                                                initPosEinh: angebotPosition.posEinh,
                                                                initPosMenge: angebotPosition.posMenge,
                                                            });
                                                        }}
                                                    />
                                                )
                                            ) : null}
                                        </td>
                                    )}

                                    {allowEdit && (
                                        <>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                {angebotPosition.pos > 1 && (
                                                    <UpIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={async () => {
                                                            await moveUpAngebotPosition(
                                                                angebotPosition.aNr,
                                                                angebotPosition.pos,
                                                            );
                                                            if (refetch) {
                                                                refetch();
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </td>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                <EditIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={() => setAngebotPositionEditData(angebotPosition)}
                                                />
                                            </td>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                <DeleteIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={() => setAngebotPositionDeleteData(angebotPosition)}
                                                />
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
            {angebotPositionEditData !== null && (
                <AngebotPositionEdit
                    kunde={kunde}
                    aNr={angebotPositionEditData.aNr}
                    pos={angebotPositionEditData.pos}
                    onClose={(_refetch) => {
                        setAngebotPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {angebotPositionDeleteData !== null && (
                <AngebotPositionDelete
                    angebotPosition={angebotPositionDeleteData}
                    onClose={(_refetch) => {
                        setAngebotPositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {terminEditData !== null && (
                <TerminEdit
                    defaultKNr={terminEditData.kNr}
                    zfKaNr={terminEditData.zfKaNr}
                    initUserKz={terminEditData.initUserKz}
                    initKaNr={terminEditData.initKaNr}
                    initTerminArt={terminEditData.initTerminArt}
                    initTerminBetreff={terminEditData.initTerminBetreff}
                    initArtikelGruppeNr={terminEditData.initArtikelGruppeNr}
                    initArtikelNr={terminEditData.initArtikelNr}
                    initPosText={terminEditData.initPosText}
                    initPosEinh={terminEditData.initPosEinh}
                    initPosMenge={terminEditData.initPosMenge}
                    onClose={(doRefetch) => {
                        setTerminEditData(null);
                        if (doRefetch) {
                            refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
